import { Autocomplete, type AutocompleteProps } from '@dev-spendesk/grapes';
import React, { type ReactNode } from 'react';

import type { Option } from './option';
import { useSearch } from './useSearch';

type Props<T extends Option> = Omit<
  AutocompleteProps<T>,
  'onSearch' | 'renderNoOptions' | 'onAddOption' | 'renderAddOption'
> &
  (
    | {
        renderAddOption(rawValue: string): ReactNode; // should always return a DropdownItem component
        onAddOption(newOptionLabel: string): T | Promise<T>;
        renderNoOptions?(rawValue: string): ReactNode;
        onInputChanged?: (value?: string) => void;
      }
    | {
        renderNoOptions(rawValue: string): ReactNode;
        renderAddOption?: never;
        onAddOption?: never;
        onInputChanged?: (value?: string) => void;
      }
  );

export const AutocompleteSearch = <T extends Option>({
  options,
  onInputChanged,
  ...props
}: Props<T>) => {
  const { filteredOptions, handleSearch } = useSearch(options);
  return (
    <Autocomplete
      {...props}
      options={filteredOptions}
      onSearch={(value) => {
        if (onInputChanged) {
          onInputChanged(value);
        }
        return handleSearch(value);
      }}
    />
  );
};
